/**
 * PickupEvents - Pickup System Socket Event Handlers
 *
 * Handles events related to item pickups:
 * - pickupCollected: A pickup was collected by a player
 * - pickupSpawned: A new pickup appeared in the world
 * - pickupRemoved: A pickup was removed from the world
 * - pickupFailed: Pickup attempt failed (shown to the attempting player)
 */

import { configRegistry } from '../../core/ConfigRegistry.js';
import { ClientLogger } from '../../logger/ClientLogger.js';

const pickupEventsLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const pickupEventsLogger = ClientLogger.getLogger('pickup') || pickupEventsLoggerFallback;

const PickupEvents = {
  /**
   * Register all pickup-related event handlers
   * @param {Socket} socket - Socket.io socket instance
   * @param {GameState} gameState - Game state container
   * @param {Object} callbacks - Callback functions
   * @param {Function} callbacks.showNotification - Show UI notification
   * @param {Function} callbacks.playSound - Play a sound effect
   * @param {Function} callbacks.loadWorldObjectImage - Load world object image into cache
   */
  register(socket, gameState, callbacks) {
    const { showNotification, playSound, loadWorldObjectImage } = callbacks;
    const activePickupSounds = [];

    const stopPickupSounds = () => {
      for (const sound of activePickupSounds) {
        try {
          sound.pause();
          sound.currentTime = 0;
        } catch (err) {
          // Already stopped
        }
      }
      activePickupSounds.length = 0;
    };

    // A pickup was collected (broadcast to all players)
    socket.on('pickupCollected', (data) => {
      if (!data || !data.pickupId) return;

      pickupEventsLogger.info(`[PickupEvents] Pickup collected: ${data.pickupId} by ${data.playerId}`);

      // Remove the pickup from world objects
      if (gameState.worldObjects) {
        // worldObjects might be a Map or array depending on implementation
        if (gameState.worldObjects instanceof Map) {
          gameState.worldObjects.delete(data.pickupId);
        } else if (Array.isArray(gameState.worldObjects)) {
          const index = gameState.worldObjects.findIndex(obj => obj.id === data.pickupId);
          if (index !== -1) {
            gameState.worldObjects.splice(index, 1);
          }
        }
      }

      // If it was collected by the local player, show feedback
      if (data.playerId === gameState.localId) {
        // Play pickup sound
        if (playSound) {
          playSound('pickup');
        } else {
          // Default pickup sound
          const audio = new Audio('assets/sound/pickup/ability_pickup.wav');
          audio.volume = 0.5;
          activePickupSounds.push(audio);
          audio.play().catch(() => {});
        }

        // Show notification (optional, could be too noisy)
        // if (showNotification && data.abilityId) {
        //   showNotification(`Picked up ${data.abilityId}!`, 'success');
        // }
      }
    });

    // A new pickup spawned in the world (e.g., dropped by another player)
    socket.on('pickupSpawned', (data) => {
      if (!data || !data.pickupId) return;

      // `ref` is the 5D identity of what the pickup holds; the abilityId /
      // weaponId / pickupType fields are its pre-5D spellings, still sent for
      // one release and still read here so an older server keeps working.
      const ref = data.ref || null;
      const pickupLabel = ref || data.abilityId || data.weaponId || 'unknown';
      pickupEventsLogger.info(`[PickupEvents] Pickup spawned: ${data.pickupId} (${pickupLabel}) at (${data.x}, ${data.y})`);

      const pickupType = (ref ? ref.split(':')[0] : null) || data.pickupType || 'ability';
      let worldObject;

      if (data.worldObject && typeof data.worldObject === 'object') {
        worldObject = { ...data.worldObject };
        worldObject.id = worldObject.id || data.pickupId;
        worldObject.type = worldObject.type || `${pickupType}_pickup`;
        worldObject.x = worldObject.x ?? data.x;
        worldObject.y = worldObject.y ?? data.y;
        worldObject.width = worldObject.width ?? data.width ?? 64;
        worldObject.height = worldObject.height ?? data.height ?? 64;
        worldObject.sensorRadius = worldObject.sensorRadius ?? data.sensorRadius ?? 52;
        worldObject.ref = worldObject.ref || ref;
        worldObject.abilityId = worldObject.abilityId || data.abilityId || null;
        worldObject.weaponId = worldObject.weaponId || data.weaponId || null;
        if (typeof worldObject.stacks !== 'number') {
          worldObject.stacks = data.stacks || 1;
        }
        if (!worldObject.category && data.category) {
          worldObject.category = data.category;
        }
      } else {
        worldObject = {
          id: data.pickupId,
          type: `${pickupType}_pickup`,
          ref,
          abilityId: data.abilityId || null,
          weaponId: data.weaponId || null,
          category: data.category,
          stacks: data.stacks || 1,
          x: data.x,
          y: data.y,
          width: data.width || 64,
          height: data.height || 64,
          sensorRadius: data.sensorRadius || 52,
          graphic: data.graphic,
          opacity: 1.0,
          collidable: false,
          isSensor: true
        };
      }

      // Determine graphic path if missing (primarily for ability pickups)
      if (!worldObject.graphic) {
        let graphic = null;

        if (typeof configRegistry !== 'undefined' && configRegistry.isLoaded() && data.abilityId) {
          graphic = configRegistry.getAbilityPickupGraphic(data.abilityId);
          if (graphic) {
            pickupEventsLogger.debug(`[PickupEvents] Using graphic from ConfigRegistry for ${data.abilityId}`);
          }
        }

        // There used to be a hardcoded id → art map here, which knew about five
        // of the seven abilities and nothing about weapons. Pickup definitions
        // are derived from the item registries now (5D), so the server always
        // sends a graphic and this is only a guard against a missing asset.
        if (!graphic) {
          pickupEventsLogger.warn(`[PickupEvents] No graphic for pickup ${pickupLabel}, using generic.png`);
        }

        // As a final fallback, use whatever the server initially sent
        worldObject.graphic = graphic || data.graphic || worldObject.graphic || 'assets/art/pickups/abilities/generic.png';
      }

      if (gameState.worldObjects) {
        if (gameState.worldObjects instanceof Map) {
          gameState.worldObjects.set(worldObject.id, worldObject);
        } else if (Array.isArray(gameState.worldObjects)) {
          gameState.worldObjects.push(worldObject);
        }
      }

      // Load the pickup graphic into the image cache
      if (loadWorldObjectImage) {
        loadWorldObjectImage(worldObject);
      }
    });

    // A pickup was removed from the world
    socket.on('pickupRemoved', (data) => {
      if (!data || !data.pickupId) return;

      pickupEventsLogger.info(`[PickupEvents] Pickup removed: ${data.pickupId}`);

      // Remove from world objects
      if (gameState.worldObjects) {
        if (gameState.worldObjects instanceof Map) {
          gameState.worldObjects.delete(data.pickupId);
        } else if (Array.isArray(gameState.worldObjects)) {
          const index = gameState.worldObjects.findIndex(obj => obj.id === data.pickupId);
          if (index !== -1) {
            gameState.worldObjects.splice(index, 1);
          }
        }
      }
    });

    // Pickup attempt failed (only sent to the attempting player)
    socket.on('pickupFailed', (data) => {
      if (!data) return;

      pickupEventsLogger.warn(`[PickupEvents] Pickup failed: ${data.reason}`);

      // Optionally show notification for failures
      // if (showNotification && data.reason) {
      //   showNotification(`Cannot pick up: ${data.reason}`, 'warning');
      // }
    });

    // A pickup expired (TTL elapsed for dropped items)
    socket.on('pickupExpired', (data) => {
      if (!data || !data.pickupId) return;

      pickupEventsLogger.info(`[PickupEvents] Pickup expired: ${data.pickupId}`);

      // Remove from world objects
      if (gameState.worldObjects) {
        if (gameState.worldObjects instanceof Map) {
          gameState.worldObjects.delete(data.pickupId);
        } else if (Array.isArray(gameState.worldObjects)) {
          const index = gameState.worldObjects.findIndex(obj => obj.id === data.pickupId);
          if (index !== -1) {
            gameState.worldObjects.splice(index, 1);
          }
        }
      }
    });

    socket.on('gameStarted', () => {
      stopPickupSounds();
    });
  }
};

// Export for use in other modules
// For now, PickupEvents is a global object loaded via script tag

export { pickupEventsLoggerFallback, pickupEventsLogger, PickupEvents };
